import {
  Dispatch, SetStateAction, useCallback, useState,
} from 'react';
import { upperFirst } from 'lodash';
import {
  useSnackbar,
  useTable,
  useTableFetch,
  useTranslations,
  useTableLocalization,
  useTablePreserverActivePageValidator,
  useTablePreserverDecoder,
  useTablePreserverEncoder,
  usePubSub,
  buildTablePreserverDecoderConfigItem,
  TablePreserverCommonInitialTableValues,
  IUseTableReturn,
  IUseTableResetTableMethodsReturn,
  IUseTableFetchReturn,
  IUseTableLocalizationReturn,
  TUseTablePreserverDecoderConfigs,
  TablePreserverDecoderConfigItemTypeEnum,
  TSetSearchParams,
} from '@uniqkey-frontend/shared-app';
import {
  GroupsByVaultIdOrderProperty,
  GetGroupsByVaultIdResponseModel,
} from '@uniqkey-backend-organization-web/api-client';
import { useSearchParams } from 'react-router-dom';
import PubSubEventEnum from '../../../enums/PubSubEventEnum';
import {
  IGetGroupsByVaultIdParams,
  TGetGroupsByVaultIdMethod,
} from '../../useEmployeeGroupsAPI/interfaces';
import {
  IOrganizationLoginGroupsTableRow,
} from '../../../components/tables/OrganizationLoginGroupsTable';
import useEmployeeGroupsAPI from '../../useEmployeeGroupsAPI';

interface IPersistentFilters extends Pick<
  IGetGroupsByVaultIdParams, 'vaultId'
> {}

export interface IUseOrganizationLoginGroupsTableProps {
  persistentFilters: IPersistentFilters;
}

export interface IUseOrganizationLoginGroupsTableReturn extends
  Omit<IUseTableReturn<IOrganizationLoginGroupsTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Pick<IUseTableResetTableMethodsReturn, 'resetActivePage'>,
  Omit<IUseTableFetchReturn<GetGroupsByVaultIdResponseModel>, 'data'>
{
  groups: IUseTableFetchReturn<GetGroupsByVaultIdResponseModel>['data'];
  selectedGroups: IUseTableReturn<IOrganizationLoginGroupsTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  searchQuery: IGetGroupsByVaultIdParams['searchQuery'];
  setSearchQuery: Dispatch<SetStateAction<
    IGetGroupsByVaultIdParams['searchQuery']>
  >;
}

interface ITablePreservationConfigs extends
  Omit<IGetGroupsByVaultIdParams,
    'page' | 'pageLength' | 'vaultId' | 'orderPropertyName' | 'isDescending' | 'cancelToken'
  >,
  Pick<
    IUseTableReturn<IOrganizationLoginGroupsTableRow>,
    'columnOrderBy' | 'columnOrderDirection'
  >
{
  activePage: IGetGroupsByVaultIdParams['page'];
  perPage: IGetGroupsByVaultIdParams['pageLength'];
}

const PRESERVATION_CONFIGS: TUseTablePreserverDecoderConfigs<ITablePreservationConfigs> = {
  activePage: true,
  perPage: true,
  searchQuery: true,
  columnOrderBy: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: GroupsByVaultIdOrderProperty },
  ),
  columnOrderDirection: true,
};

const useOrganizationLoginGroupsTable = (
  props: IUseOrganizationLoginGroupsTableProps,
): IUseOrganizationLoginGroupsTableReturn => {
  const {
    persistentFilters,
  } = props;

  const { t } = useTranslations();
  const { showError } = useSnackbar();
  const { getGroupsByVaultId } = useEmployeeGroupsAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnRequestError = useCallback(() => {
    showError({ text: t('common.somethingWentWrong') });
  }, [showError, t]);

  const {
    activePage: initialActivePage,
    perPage: initialPerPage,
    searchQuery: initialSearchQuery,
    columnOrderBy: initialColumnOrderBy,
    columnOrderDirection: initialColumnOrderDirection,
  } = useTablePreserverDecoder<ITablePreservationConfigs>(searchParams, PRESERVATION_CONFIGS);

  const [searchQuery, setSearchQuery] = useState<
    IGetGroupsByVaultIdParams['searchQuery']
  >(() => initialSearchQuery);

  const {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedRows: selectedGroups,
    resetTableMethods,
    ...restTableProps
  } = useTable<IOrganizationLoginGroupsTableRow>({
    initialActivePage,
    initialPerPage,
    initialColumnOrderBy,
    initialColumnOrderDirection,
  });

  const {
    resetTable,
    resetActivePage,
  } = resetTableMethods;

  const handleTableReset = useCallback(() => {
    resetTable();
    setSearchQuery(TablePreserverCommonInitialTableValues.searchQuery);
  }, [resetTable]);
  usePubSub(PubSubEventEnum.RESET_TABLE, handleTableReset);

  const {
    data: groups, isLoading, isFetchedAfterMount, total, refetch,
  } = useTableFetch<
    IGetGroupsByVaultIdParams,
    TGetGroupsByVaultIdMethod,
    GetGroupsByVaultIdResponseModel
  >({
    request: getGroupsByVaultId,
    params: {
      page: activePage,
      pageLength: perPage,
      searchQuery,
      orderPropertyName: GroupsByVaultIdOrderProperty[
        upperFirst(columnOrderBy) as keyof typeof GroupsByVaultIdOrderProperty
      ],
      isDescending: columnOrderDirection ? columnOrderDirection === 'desc' : undefined,
      ...persistentFilters,
    },
    onRequestError: handleOnRequestError,
  });

  const { localization } = useTableLocalization({
    searchQuery,
    isLoading,
  });

  useTablePreserverActivePageValidator({
    activePage, resetActivePage, perPage, total, isFetchedAfterMount,
  });

  useTablePreserverEncoder<ITablePreservationConfigs>(setSearchParams as TSetSearchParams, {
    activePage,
    perPage,
    searchQuery,
    columnOrderBy,
    columnOrderDirection,
  });

  return {
    groups,
    isLoading,
    total,
    refetch,
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedGroups,
    localization,
    searchQuery,
    setSearchQuery,
    resetActivePage,
    ...restTableProps,
  };
};

export default useOrganizationLoginGroupsTable;
